import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pluralize } from "@common/helpers/string";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import type { ServiceTemplateScheduleBulkActionType } from "@common/contracts/service-template-schedule.contract";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { useBulkServiceTemplateScheduleActionMutation } from "@api/serviceTemplateSchedule.api";
import { ServiceTemplateScheduleLeafRow } from "./serviceTemplateSchedulesViewConfig";

interface UseServiceTemplateScheduleBulkActionsParams {
    organizationId: string | null | undefined;
    selectedRows: ServiceTemplateScheduleLeafRow[];
    clearSelection: () => void;
}

/** Only ACTIVE schedules can be paused. */
const isEligibleForPause = (row: ServiceTemplateScheduleLeafRow) =>
    row.status === ServiceTemplateRecurrenceStatus.ACTIVE;

/** Only PAUSED schedules can be resumed. */
const isEligibleForResume = (row: ServiceTemplateScheduleLeafRow) =>
    row.status === ServiceTemplateRecurrenceStatus.PAUSED;

export function useServiceTemplateScheduleBulkActions({
    organizationId,
    selectedRows,
    clearSelection,
}: UseServiceTemplateScheduleBulkActionsParams) {
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const { toast: successToast, error: errorToast } = useToaster();
    const {
        mutateAsync: bulkActionMutateAsync,
        isPending: isBulkActionPending,
    } = useBulkServiceTemplateScheduleActionMutation();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const pauseEligibleIds = useMemo(
        () => selectedRows.filter(isEligibleForPause).map((row) => row.id),
        [selectedRows],
    );
    const resumeEligibleIds = useMemo(
        () => selectedRows.filter(isEligibleForResume).map((row) => row.id),
        [selectedRows],
    );
    const deleteEligibleIds = useMemo(
        () => selectedRows.map((row) => row.id),
        [selectedRows],
    );

    const runBulkAction = useCallback(
        async (
            scheduleIds: string[],
            action: ServiceTemplateScheduleBulkActionType,
            verbPastTense: string,
        ) => {
            if (!organizationId || scheduleIds.length === 0) {
                return;
            }
            closeCommands();
            try {
                const result = await bulkActionMutateAsync({
                    organizationId,
                    scheduleIds,
                    action,
                });
                if (result.succeededIds.length > 0) {
                    successToast(
                        `${verbPastTense} ${result.succeededIds.length} ${pluralize(
                            "schedule",
                            result.succeededIds.length,
                        )}`,
                    );
                    clearSelection();
                }
                if (result.failed.length > 0) {
                    errorToast(
                        `Failed to ${action} ${result.failed.length} ${pluralize(
                            "schedule",
                            result.failed.length,
                        )}`,
                    );
                }
            } catch (e) {
                errorToast(
                    e instanceof Error
                        ? e.message
                        : "Failed to perform bulk action on schedules",
                );
            }
        },
        [
            organizationId,
            bulkActionMutateAsync,
            closeCommands,
            successToast,
            errorToast,
            clearSelection,
        ],
    );

    const handleBulkPause = useCallback(
        () => void runBulkAction(pauseEligibleIds, "pause", "Paused"),
        [runBulkAction, pauseEligibleIds],
    );

    const handleBulkResume = useCallback(
        () => void runBulkAction(resumeEligibleIds, "resume", "Resumed"),
        [runBulkAction, resumeEligibleIds],
    );

    const handleBulkDeleteRequest = useCallback(() => {
        closeCommands();
        setShowDeleteConfirm(true);
    }, [closeCommands]);

    const handleBulkDeleteConfirm = useCallback(async () => {
        setShowDeleteConfirm(false);
        await runBulkAction(deleteEligibleIds, "delete", "Deleted");
    }, [runBulkAction, deleteEligibleIds]);

    const handleOpenBulkActions = useCallback(() => {
        if (selectedRows.length === 0) {
            return;
        }
        openCommands(CommandsDirectory.SCHEDULE_BULK, {
            selectedScheduleCount: selectedRows.length,
            onScheduleBulkPause:
                pauseEligibleIds.length > 0 ? handleBulkPause : undefined,
            onScheduleBulkResume:
                resumeEligibleIds.length > 0 ? handleBulkResume : undefined,
            onScheduleBulkDelete:
                deleteEligibleIds.length > 0
                    ? handleBulkDeleteRequest
                    : undefined,
        });
    }, [
        openCommands,
        selectedRows.length,
        pauseEligibleIds.length,
        resumeEligibleIds.length,
        deleteEligibleIds.length,
        handleBulkPause,
        handleBulkResume,
        handleBulkDeleteRequest,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedRows.length > 0) {
            registerOpenOverride(() => handleOpenBulkActionsRef.current());
            return () => unregisterOpenOverride();
        }
        unregisterOpenOverride();
    }, [selectedRows.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        showDeleteConfirm,
        setShowDeleteConfirm,
        handleBulkDeleteConfirm,
        deleteEligibleCount: deleteEligibleIds.length,
        isBulkActionPending,
    };
}
